import { loadFontFamily, usePublisherStyling } from '@ad-rendering/publisher-style';
import { usePayload } from '@web-rendering/groundcontrol-core';
import { reportError } from '@web-rendering/groundcontrol-monitoring';
import { recreateChildScriptTags } from '@web-rendering/groundcontrol-utils';
import { forwardRef } from 'preact/compat';
import { useEffect, useRef } from 'preact/hooks';
import type { TemplateProps } from './types';
import { UnenhancedTemplate } from './unenhanced/unenhanced.component';

/**
 * TemplateUnenhancedOnly - Template component for unenhanced-only builds
 *
 * This component ONLY imports and renders the UnenhancedTemplate.
 * It does NOT import EnhancedTemplate or any enhancement components, resulting in
 * the smallest possible bundle size.
 *
 * Use this template in unenhanced builds where you know the creative will
 * never have an enhancement (e.g., banner-unenhanced).
 *
 * Bundle Impact:
 * - Does NOT bundle: EnhancedTemplate, any enhancement components
 * - ONLY bundles: UnenhancedTemplate (minimal)
 */
export const Template = forwardRef<HTMLDivElement, TemplateProps>((props: TemplateProps, ref) => {
  const adWrapperRef = useRef<HTMLElement | null>(null);
  const { styles, error: publisherStyleError } = usePublisherStyling();
  const payload = usePayload();

  useEffect(() => {
    if (adWrapperRef.current === null) return;
    // @ts-expect-error: Property 'base' does not exist on type 'HTMLElement' (is it correct on a Fragment?)
    const baseElement = adWrapperRef.current.base;
    if (baseElement) recreateChildScriptTags(baseElement.parentElement);
  }, [adWrapperRef]);

  useEffect(() => {
    if (publisherStyleError) {
      reportError(publisherStyleError, {
        adserverRequestId: payload?.id,
        partnerId: payload?.creative.partnerId,
        networkId: payload?.network.networkId,
        callerId: payload?.network.callerId
      });
      return;
    }

    if (!styles || !styles.fontFamily) return;
    loadFontFamily(styles.fontFamily);
  }, [styles, publisherStyleError]);

  const { adserverRequestId, creative, network, country, className, children } = props;
  const commonProps = { adserverRequestId, creative, network, className, country, children };

  return (
    <div
      data-testid="str-compiled-template"
      className="str-compiled-template"
      style={{ width: '100%', height: '100%' }}
      ref={ref}
    >
      <UnenhancedTemplate {...commonProps} />
    </div>
  );
});
